import { useContext, useEffect, useMemo, useState } from 'react';

import usePagination from '@/hooks/usePagination';
import { usePendingTransactions } from '@/hooks/useRewardCards';
import PersonContext from '@/pages/Person/PersonContext';
import TransactionItem from '@/pages/TransactionList/components/TransactionItem';
import Alert from '@/ui/components/Alert';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import Paginator from '@/ui/components/Paginator';

const PendingTransactionsList = () => {
  const { person } = useContext(PersonContext);
  const [page, setPage] = useState('1');
  const [totalPages, setTotalPages] = useState<number>(0);
  const { data, isError, isPending, isSuccess } = usePendingTransactions(person.id, { page });

  useEffect(() => {
    if (isSuccess) {
      setPage(data?.page.toString() ?? '1');
      setTotalPages(data?.total_pages ?? 0);
    }
  }, [data?.page, data?.total_pages, isSuccess]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(+page, totalPages);

  useEffect(() => {
    setPage(currentPage.toString());
  }, [currentPage]);

  const renderData = useMemo(() => {
    if (isPending) {
      return <LoadingSkeleton spinnerProps={{ label: 'Loading pending transactions...' }} />;
    }

    if (isError) {
      return <Alert level="error">There was an error loading pending transactions.</Alert>;
    }

    if (data?.results?.length === 0) {
      return <Alert level="info">No pending transactions found.</Alert>;
    }

    return (
      <div className="flex flex-col space-y-4 my-2">
        {data?.results.map((transaction) => {
          return (
            <TransactionItem
              key={transaction.id}
              transaction={transaction}
              personTimeZone={person.time_zone}
            />
          );
        })}
      </div>
    );
  }, [data?.results, isError, isPending, person.time_zone]);

  return (
    <div className="mb-4">
      <Heading value={3} text="Pending" />

      {renderData}

      <Paginator
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}
      />
    </div>
  );
};

export default PendingTransactionsList;
